import React from "react";

import { G } from "react-native-svg";

import TouchableCircle from "../TouchableCircle";
import type { BoardNode } from "./boardModel";

export interface PlacedNode extends BoardNode {
  /** Board-unit position (px on the live board). */
  px: number;
  py: number;
  r: number;
}

interface BoardNodesProps {
  nodes: PlacedNode[];
  /** The won target's fill during the win wave; null: the model decides. */
  targetFill?: { node: string; level: number } | null;
  /** Per-node pop/wave scale; 1 when absent. */
  scaleOf?: (id: string) => number;
  fillDuration: number;
  /** Motion allowed: the current node breathes (off under reduced motion). */
  breathe?: boolean;
  unitsPerPx?: number;
  onPress: (id: string) => void;
}

/**
 * Board nodes: solid discs, the target an empty ring. Pops and the wave scale
 * the node's shape (ring included) on a parent G; the current node's breathing
 * animates its radius underneath, so the two compose instead of fighting.
 */
const BoardNodes: React.FC<BoardNodesProps> = ({
  nodes,
  scaleOf,
  targetFill = null,
  fillDuration,
  breathe = true,
  unitsPerPx = 1,
  onPress,
}) => (
  <G>
    {nodes.map((n) => {
      const s = scaleOf ? scaleOf(n.id) : 1;
      return (
        <G key={n.id} transform={`translate(${n.px},${n.py})`}>
          <G transform={s !== 1 ? `scale(${s})` : undefined}>
            <TouchableCircle
              r={n.r}
              fill={n.fill}
              hollow={n.hollow}
              fillLevel={
                n.endpoint === "target" && targetFill?.node === n.id
                  ? targetFill.level
                  : n.filled
                    ? 1
                    : 0
              }
              breathing={breathe && n.breathing}
              fillDuration={fillDuration}
              unitsPerPx={unitsPerPx}
              onPress={() => onPress(n.id)}
              testID={`board-node-${n.id}`}
            />
          </G>
        </G>
      );
    })}
  </G>
);

export default BoardNodes;
